import type { Project } from '../core/model';
import type { ProjectRepository, ProjectSummary } from './api';
import { createDbOpener } from './db';
import { migrateProject } from './migrate';

const LAST_OPENED_KEY = 'lastOpenedId';

export function createProjectRepository(): ProjectRepository {
  const open = createDbOpener();

  return {
    async list() {
      const db = await open();
      const all = await db.getAll('projectSummaries');
      return all.sort((a, b) => b.updatedAt - a.updatedAt);
    },

    async load(id) {
      const db = await open();
      const raw = await db.get('projects', id);
      if (raw === undefined) return null;
      try {
        return migrateProject(raw);
      } catch (err) {
        throw new Error(`Project "${id}" could not be loaded: ${err instanceof Error ? err.message : String(err)}`);
      }
    },

    async save(project: Project) {
      const db = await open();
      const summary: ProjectSummary = {
        id: project.id,
        name: project.name,
        updatedAt: project.updatedAt,
        sceneCount: project.sceneOrder.length,
      };
      const tx = db.transaction(['projects', 'projectSummaries'], 'readwrite');
      void tx.objectStore('projects').put(project);
      void tx.objectStore('projectSummaries').put(summary);
      await tx.done;
    },

    async delete(id) {
      const db = await open();
      const tx = db.transaction(['projects', 'projectSummaries', 'meta'], 'readwrite');
      void tx.objectStore('projects').delete(id);
      void tx.objectStore('projectSummaries').delete(id);
      const meta = tx.objectStore('meta');
      if ((await meta.get(LAST_OPENED_KEY)) === id) void meta.delete(LAST_OPENED_KEY);
      await tx.done;
    },

    async getLastOpenedId() {
      const db = await open();
      const id = await db.get('meta', LAST_OPENED_KEY);
      return typeof id === 'string' ? id : null;
    },

    async setLastOpenedId(id) {
      const db = await open();
      if (id === null) await db.delete('meta', LAST_OPENED_KEY);
      else await db.put('meta', id, LAST_OPENED_KEY);
    },
  };
}
